import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import { ChevronDown, Zap, Clock, Video, AlertCircle } from 'lucide-react';
import { cn } from 'src/lib/utils';
import styles from './PipelineOverviewHeader.module.css';

// "Today at a glance" tiles — all four backed by
// GET .../dashboard/assignments/candidates-pipeline/summary
// (useCandidatesPipelineSummary, called in AssignmentPipeline.jsx and
// passed down as the needsYouToday/waitingOnCandidate/interviewsThisWeek/
// autoClosingTonight props below).
const GLANCE_TILES = [
  { key: 'needs_you', icon: Zap, label: 'Needs you today', tint: 'tintA' },
  {
    key: 'waiting',
    icon: Clock,
    label: 'Waiting on candidate',
    tint: 'tintB',
  },
  {
    key: 'interviews',
    icon: Video,
    label: 'Interviews this week',
    tint: 'tintB',
  },
  {
    key: 'closing',
    icon: AlertCircle,
    label: 'Auto-closing tonight',
    tint: 'tintR',
  },
];

const formatDeadline = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : format(d, 'dd MMM');
};

// Requirement/assignment overview banner — sits between PageHeader and the
// tab bar. Styling is lifted class-for-class from screening-pipeline-v3
// .html's own §10/§18 CSS (page-head/meta-row/fill/mrow/mcard — see
// PipelineOverviewHeader.module.css), not this app's Tailwind ds-* tokens.
//
// `max-w-[1100px] mx-auto` on the root (added alongside the template's own
// px-6/pt-5) mirrors PageContainer.jsx's own width cap — this banner sits
// OUTSIDE PageContainer (it needs to render above the tab bar, not inside
// its bg-muted body), so it never inherited that constraint on its own.
// Without it, on a large/ultrawide monitor this banner stretched edge-to-
// edge while the tab bar + table directly below it (inside PageContainer)
// stayed capped and centered — the two visibly disagreed on where the page
// content ends. Matching the same max-width + centering here (not a
// slightly different number) is what makes the banner's edges actually line
// up with the table's, not just "similarly narrow."
//
// Real fields come from GET .../requirement-locations (assignment_info +
// assignment_locations) and GET .../flow-statuses (flow.name); the
// "shortlisted / in play / more needed" split is derived by aggregating
// candidates_requested/submitted/passed across every location on the
// assignment, not a literal API field — "shortlisted" is approximated from
// candidates_passed since the API has no field named that. Everything else
// ("no assessment" copy) is a placeholder, per instruction.
const PipelineOverviewHeader = ({
  assignmentInfo,
  assignmentLocations,
  flowName,
  needsYouToday,
  waitingOnCandidate,
  interviewsThisWeek,
  autoClosingTonight,
}) => {
  const [glanceVisible, setGlanceVisible] = useState(true);

  const glanceValues = {
    needs_you: needsYouToday,
    waiting: waitingOnCandidate,
    interviews: interviewsThisWeek,
    closing: autoClosingTonight,
  };

  const totals = assignmentLocations.reduce(
    (acc, loc) => ({
      requested: acc.requested + (loc.candidates_requested || 0),
      submitted: acc.submitted + (loc.candidates_submitted || 0),
      passed: acc.passed + (loc.candidates_passed || 0),
    }),
    { requested: 0, submitted: 0, passed: 0 }
  );
  const inPlay = Math.max(totals.submitted - totals.passed, 0);
  const moreNeeded = Math.max(totals.requested - totals.submitted, 0);
  const progressPct =
    totals.requested > 0 ? (totals.passed / totals.requested) * 100 : 0;

  const primaryLocation = assignmentLocations[0];
  const deadlineLabel = formatDeadline(assignmentInfo?.deadline);

  return (
    <div className={cn(styles.root, 'max-w-[1100px] mx-auto px-6 pt-5')}>
      <div className={styles.pageHead}>
        <div style={{ flex: 1, minWidth: 280 }}>
          <h1 className={styles.h1}>Screening pipeline</h1>
          <p className={styles.pageDesc}>
            Everyone you&apos;ve added to this requirement, and what each one is
            waiting on right now.
          </p>
          <div className={styles.metaRow}>
            {assignmentInfo?.registration_id && (
              <span className={styles.metaId}>
                {assignmentInfo.registration_id}
              </span>
            )}
            {assignmentInfo?.title && (
              <span className={styles.metaPill}>{assignmentInfo.title}</span>
            )}
            {primaryLocation && (
              <span className={styles.metaPill}>
                {primaryLocation.location_name} ·{' '}
                {primaryLocation.employment_type}
              </span>
            )}
            {flowName && (
              <span className={styles.metaPill}>
                <b className={styles.metaPillStrong}>{flowName}</b> flow · no
                assessment
              </span>
            )}
            {deadlineLabel && (
              <span className={styles.metaPill}>
                Closes <b className={styles.metaPillStrong}>{deadlineLabel}</b>
              </span>
            )}
          </div>
        </div>

        {totals.requested > 0 && (
          <div className={styles.fill}>
            <div className={styles.fh}>
              <span className={styles.fv}>{totals.passed}</span>
              <span className={styles.fk}>
                of {totals.requested} positions shortlisted
              </span>
            </div>
            <div
              className={styles.fbar}
              role="img"
              aria-label={`${totals.passed} of ${totals.requested} positions covered by shortlisted candidates`}
            >
              <i
                className={styles.fbarDone}
                style={{ width: `${progressPct}%` }}
              />
            </div>
            <div className={styles.fleg}>
              <span>
                <i style={{ background: 'var(--ph-green)' }} />
                {totals.passed} shortlisted
              </span>
              <span>
                <i style={{ background: 'var(--ph-brand)' }} />
                {inPlay} in play
              </span>
              <span>
                <i style={{ background: 'var(--ph-track)' }} />
                {moreNeeded} more needed
              </span>
            </div>
          </div>
        )}
      </div>

      <div className={styles.mrowHead}>
        <h2 className={styles.mrowHeadH2} id="pipelineGlanceTitle">
          Today at a glance
        </h2>
        <button
          type="button"
          className={styles.mtoggle}
          aria-expanded={glanceVisible}
          aria-controls="pipelineGlanceRow"
          onClick={() => setGlanceVisible((v) => !v)}
        >
          <ChevronDown
            className={cn(
              styles.mtoggleIcon,
              !glanceVisible && styles.mtoggleIconClosed
            )}
          />
          {glanceVisible ? 'Hide' : 'Show'}
        </button>
      </div>
      <div
        className={cn(styles.mrowWrap, !glanceVisible && styles.mrowWrapClosed)}
      >
        <div
          className={styles.mrow}
          id="pipelineGlanceRow"
          role="group"
          aria-labelledby="pipelineGlanceTitle"
          inert={!glanceVisible ? '' : undefined}
        >
          {GLANCE_TILES.map((tile) => {
            const Icon = tile.icon;
            const value = glanceValues[tile.key] ?? tile.value ?? 0;
            return (
              <div key={tile.key} className={styles.mcard}>
                <div className={cn(styles.mt, styles[tile.tint])}>
                  <Icon />
                </div>
                <div className={styles.ml}>{tile.label}</div>
                <div className={styles.mv}>{value}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

PipelineOverviewHeader.propTypes = {
  assignmentInfo: PropTypes.shape({
    title: PropTypes.string,
    registration_id: PropTypes.string,
    deadline: PropTypes.string,
  }),
  assignmentLocations: PropTypes.arrayOf(
    PropTypes.shape({
      location_name: PropTypes.string,
      employment_type: PropTypes.string,
      candidates_requested: PropTypes.number,
      candidates_submitted: PropTypes.number,
      candidates_passed: PropTypes.number,
    })
  ),
  flowName: PropTypes.string,
  needsYouToday: PropTypes.number,
  waitingOnCandidate: PropTypes.number,
  interviewsThisWeek: PropTypes.number,
  autoClosingTonight: PropTypes.number,
};

PipelineOverviewHeader.defaultProps = {
  assignmentInfo: null,
  assignmentLocations: [],
  flowName: null,
  needsYouToday: 0,
  waitingOnCandidate: 0,
  interviewsThisWeek: 0,
  autoClosingTonight: 0,
};

export default PipelineOverviewHeader;
